import React, { useState, useMemo } from 'react';
import Seo from '../../components/Seo';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Search, Globe, Phone, MessageSquare, Mail } from 'lucide-react';
import { Textarea } from '../../components/ui/textarea';
import { useCountries } from '../../hooks/useCountries';
import { useToast } from '../../hooks/use-toast';
import NumberCountryTable, { ResultsLine, matchesCountry } from '../../components/numbers/NumberCountryTable';
import { useLeadGuard } from '../../components/LeadGuard';

const FILTERS = [['all', 'All'], ['local', 'Local'], ['national', 'National'], ['mobile', 'Mobile'], ['twoway', '2-Way'], ['itfs', 'Toll-Free'], ['uifn', 'UIFN']];
// Filter key -> number group preselected in Info and Order.
const FILTER_GROUP = { local: 'local', national: 'national', mobile: 'mobile', twoway: '2way', itfs: 'tollfree', uifn: 'uifn' };

const AllPhoneNumbers = () => {
  const { toast } = useToast();
  const { countries, loading } = useCountries();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const filteredCountries = useMemo(() => {
    let filtered = countries;

    if (searchTerm) {
      filtered = filtered.filter((c) => matchesCountry(c, searchTerm));
    }

    if (filterType !== 'all') {
      filtered = filtered.filter(c => c[filterType] === true);
    }

    return filtered;
  }, [searchTerm, filterType, countries]);

  const stats = useMemo(() => {
    return {
      totalCountries: countries.length,
      local: countries.filter(c => c.local).length,
      national: countries.filter(c => c.national).length,
      mobile: countries.filter(c => c.mobile).length,
      twoway: countries.filter(c => c.twoway).length,
      itfs: countries.filter(c => c.itfs).length,
      uifn: countries.filter(c => c.uifn).length
    };
  }, [countries]);

  const handleInquiry = (country, productType, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setSelectedProduct({ country, productType });
    setInquiryForm({
      name: '',
      email: '',
      phone: '',
      message: `I'm interested in ${productType} number for ${country}`
    });
    setInquiryOpen(true);
  };

  const { guard, guardFields, resetGuard } = useLeadGuard();
  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/public/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...guardFields(),
          name: inquiryForm.name,
          email: inquiryForm.email,
          phone: inquiryForm.phone,
          company: inquiryForm.company,
          country: selectedProduct?.country,
          service_interest: selectedProduct?.productType,
          message: inquiryForm.message,
          source: 'website'
        }),
      });

      resetGuard();
      if (!response.ok) throw new Error('Failed to submit inquiry');

      toast({
        title: "Inquiry Sent!",
        description: "Our team will contact you within 24 hours.",
      });
      setInquiryOpen(false);
      setInquiryForm({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit inquiry. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleFormChange = (e) => {
    setInquiryForm({
      ...inquiryForm,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen pt-24 pb-16">
      <Seo
        title="All Phone Number Types and Countries"
        description="Compare local, national, mobile, 2-way, toll-free and UIFN numbers and find which types VOIP2 offers in each country."
        path="/phone-numbers/all"
        breadcrumbs={[{ name: 'Phone Numbers', path: '/phone-numbers/all' }]}
      />
      {/* Hero Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-12">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold mb-6" style={{ color: '#0a1f2e' }}>
            All Phone Numbers
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Access virtual phone numbers in over 90 countries worldwide. Choose from local, national, mobile, 2-way, toll-free, and UIFN numbers.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <Globe className="w-8 h-8 mx-auto mb-2 text-[#0a1f2e]" />
              <div className="text-3xl font-bold text-[#0a1f2e]">{stats.totalCountries}</div>
              <div className="text-sm text-gray-600 mt-1">Countries</div>
            </Card>
            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <Phone className="w-8 h-8 mx-auto mb-2 text-[#0a1f2e]" />
              <div className="text-3xl font-bold text-[#0a1f2e]">{stats.local}</div>
              <div className="text-sm text-gray-600 mt-1">Local</div>
            </Card>
            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <Phone className="w-8 h-8 mx-auto mb-2 text-[#0a1f2e]" />
              <div className="text-3xl font-bold text-[#0a1f2e]">{stats.national}</div>
              <div className="text-sm text-gray-600 mt-1">National</div>
            </Card>
            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <Phone className="w-8 h-8 mx-auto mb-2 text-[#0a1f2e]" />
              <div className="text-3xl font-bold text-[#0a1f2e]">{stats.mobile}</div>
              <div className="text-sm text-gray-600 mt-1">Mobile</div>
            </Card>
            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 text-[#0a1f2e]" />
              <div className="text-3xl font-bold text-[#0a1f2e]">{stats.twoway}</div>
              <div className="text-sm text-gray-600 mt-1">2-Way</div>
            </Card>
            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <Phone className="w-8 h-8 mx-auto mb-2 text-[#0a1f2e]" />
              <div className="text-3xl font-bold text-[#0a1f2e]">{stats.itfs}</div>
              <div className="text-sm text-gray-600 mt-1">Toll-Free</div>
            </Card>
            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <Globe className="w-8 h-8 mx-auto mb-2 text-[#0a1f2e]" />
              <div className="text-3xl font-bold text-[#0a1f2e]">{stats.uifn}</div>
              <div className="text-sm text-gray-600 mt-1">UIFN</div>
            </Card>
          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-8">
        <div className="max-w-7xl mx-auto">
          <Card className="p-4 sm:p-6">
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Search by country name or prefix (e.g., Germany or +49)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Number type">
                {FILTERS.map(([key, label]) => (
                  <button key={key} type="button" aria-pressed={filterType === key} onClick={() => setFilterType(key)}
                    className={`rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0a1f2e] ${filterType === key ? 'bg-[#c8ff00] text-[#0a1f2e]' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <h2 className="text-2xl font-bold mb-2" style={{ color: '#0a1f2e' }}>Countries and number types</h2>
            <div className="mb-4">
              <ResultsLine count={filteredCountries.length} total={countries.length} loading={loading} />
            </div>

            {/* Loading State */}
            {loading && (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-4">Loading countries...</p>
              </div>
            )}

            {!loading && (
              <>
                <NumberCountryTable countries={filteredCountries} group={FILTER_GROUP[filterType]}
                  onEnquire={(country, type) => handleInquiry(country, type || 'Phone')} />
                {filteredCountries.length === 0 && (
                  <p className="py-10 text-center text-gray-600">No countries match your search.</p>
                )}
              </>
            )}
          </Card>
        </div>
      </section>

      {/* Inquiry Dialog */}
      <Dialog open={inquiryOpen} onOpenChange={setInquiryOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Product Inquiry</DialogTitle>
            <DialogDescription>
              {selectedProduct && (
                <span>Request information about <strong>{selectedProduct.productType}</strong> numbers in <strong>{selectedProduct.country}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInquirySubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
              <Input id="name" name="name" value={inquiryForm.name} onChange={handleFormChange} required placeholder="John Smith" />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
              <Input id="email" name="email" type="email" value={inquiryForm.email} onChange={handleFormChange} required placeholder="john@company.com" />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <Input id="phone" name="phone" type="tel" value={inquiryForm.phone} onChange={handleFormChange} placeholder="+44 204 575 2222" />
            </div>
            <div>
              <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">Company</label>
              <Input id="company" name="company" value={inquiryForm.company} onChange={handleFormChange} placeholder="Your Company Name" />
            </div>
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
              <Textarea id="message" name="message" value={inquiryForm.message} onChange={handleFormChange} required rows={4} />
            </div>
            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={() => setInquiryOpen(false)}>Cancel</Button>
              {guard}
              <Button type="submit" style={{ backgroundColor: '#c8ff00', color: '#0a1f2e' }} className="hover:opacity-90">
                <Mail className="w-4 h-4 mr-2" />Send Inquiry
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AllPhoneNumbers;